import React, { createContext, useState, useContext, useEffect } from "react";

type ThemeType = "light" | "dark";

interface ThemeContextProps {
  theme: ThemeType;
  toggleTheme: () => void;
  setTheme: (theme: ThemeType) => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Başlangıç temasını localStorage veya sistem tercihinden al
  const getInitialTheme = (): ThemeType => {
    // SSR güvenliği için window kontrolü
    if (typeof window === "undefined") return "light";

    const savedTheme = localStorage.getItem("theme") as ThemeType;

    if (savedTheme && (savedTheme === "light" || savedTheme === "dark")) {
      return savedTheme;
    }

    // Sistem tercihi kontrolü
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)"
    ).matches;
    return prefersDark ? "dark" : "light";
  };

  const [theme, setThemeState] = useState<ThemeType>(() => getInitialTheme());

  useEffect(() => {
    // Tema değiştiğinde localStorage'a kaydet
    if (typeof window !== "undefined") {
      localStorage.setItem("theme", theme);

      // HTML document element'e tema class'ını ekle
      document.documentElement.setAttribute("data-bs-theme", theme);
      document.documentElement.classList.remove("light", "dark");
      document.documentElement.classList.add(theme);

      // Meta theme-color etiketini güncelle (mobil için)
      const metaThemeColor = document.querySelector('meta[name="theme-color"]');
      if (metaThemeColor) {
        metaThemeColor.setAttribute(
          "content",
          theme === "dark" ? "#212529" : "#ffffff"
        );
      }
    }
  }, [theme]);

  // Sistem tercihi değiştiğinde temayı güncelle
  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      // Sadece localStorage'da tema kaydı yoksa sisteme göre değiştir
      const savedTheme = localStorage.getItem("theme");
      if (!savedTheme) {
        setThemeState(e.matches ? "dark" : "light");
      }
    };

    // Modern tarayıcılar için addEventListener kullan
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    } else {
      // Eski tarayıcılar için addListener kullan
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  const toggleTheme = () => {
    setThemeState((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
  };

  const setTheme = (newTheme: ThemeType) => {
    if (newTheme === "light" || newTheme === "dark") {
      setThemeState(newTheme);
    }
  };

  const value = React.useMemo(
    () => ({
      theme,
      toggleTheme,
      setTheme,
    }),
    [theme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

// Kolay kullanım için hook
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme hook must be used within a ThemeProvider");
  }
  return context;
};

// ThemeContext'i de export et (isteğe bağlı)
export { ThemeContext };
export type { ThemeType, ThemeContextProps };
